/**
 * =============================================================================
 * setera-web
 * =============================================================================
 * Copyright (c) 2026 HG Digital/Setera
 * Todos os direitos reservados.
 *
 * Este software é confidencial e de propriedade da HG Digital/Setera.
 * Seu uso está sujeito aos termos do contrato de licença.
 * -----
 * Criado em : 19-08-2026 14:45:00
 * Autor     : Diego Brock
 * Projeto   : setera-web
 * Versão    : 1.0.0
 * =============================================================================
 */

import { Injectable } from '@angular/core';
import { CommandModel } from '../models/command-model';
import {
  STORAGE_KEY_COMANDOS_EFETIVOS,
  STORAGE_KEY_CURRENT_PERFIL,
  STORAGE_KEY_CURRENT_USER,
  STORAGE_KEY_ID_PERFIL,
} from '../storageCore/constStorageKeys';
import storageX from '../storageCore/storageX';

/**
 * Resolve os command actions permitidos ao usuário (UX).
 * Origem: `comandosEfetivos` de cada perfil no payload de `usuarios/me`.
 */
@Injectable({
  providedIn: 'root',
})
export class CommandActionAccessService {
  /**
   * Lista de códigos (`commandAction`) permitidos para o perfil ativo.
   */
  getAllowedCommandActions(): string[] {
    const stored = storageX.whereKeyIs(STORAGE_KEY_COMANDOS_EFETIVOS).get();
    if (Array.isArray(stored) && stored.length > 0) {
      return stored;
    }

    const perfil = storageX.whereKeyIs(STORAGE_KEY_CURRENT_PERFIL).get();
    if (Array.isArray(perfil?.comandosEfetivos) && perfil.comandosEfetivos.length > 0) {
      return perfil.comandosEfetivos;
    }

    const perfilId = storageX.whereKeyIs(STORAGE_KEY_ID_PERFIL).get();
    const user = storageX.whereKeyIs(STORAGE_KEY_CURRENT_USER).get();
    const perfis = Array.isArray(user?.perfis) ? user.perfis : [];
    const match = perfis.find((item) => String(item.id) === String(perfilId));
    if (Array.isArray(match?.comandosEfetivos) && match.comandosEfetivos.length > 0) {
      return match.comandosEfetivos;
    }

    return Array.isArray(stored) ? stored : [];
  }

  /**
   * Indica se o perfil possui ao menos um command action configurado.
   */
  hasAnyCommandAction(): boolean {
    return this.getAllowedCommandActions().length > 0;
  }

  /**
   * Verifica se um código de command action está liberado para o perfil.
   *
   * @param code valor de `CommandModel.commandAction`
   */
  hasCommandAction(code: string): boolean {
    if (!code) {
      return false;
    }
    return this.getAllowedCommandActions().includes(code);
  }

  /**
   * Filtra comandos do terminal pelos grants do perfil (interseção por `commandAction`).
   *
   * @param commands lista retornada pela tracking-hardware API
   */
  filterByAccess(commands: CommandModel[]): CommandModel[] {
    const allowed = new Set(this.getAllowedCommandActions());
    if (!allowed.size || !Array.isArray(commands)) {
      return [];
    }
    return commands.filter(
      (cmd) => cmd.commandAction && allowed.has(cmd.commandAction)
    );
  }
}
